<template>
  <div>
    <div
      :class="{
        'px-4 pt-4': hasPadding,
      }"
    >
      <!-- Share links -->
      <section class="mb-4">
        <h3 class="share__section-title" v-if="showHeader">
          <aha-icon-container
            class="mr-2 font-bold truncate cursor-pointer"
            size="md"
            icon-class="bg-secondary-40 text-base-0"
          >
            <aha-icon
              name="phone"
              width="16"
              height="16"
              viewBox="0 0 16 16"
            />
          </aha-icon-container>
          Share to participants
        </h3>
        <p class="share__section-description">
          Below is the short link to send to your audience.
          You can customise the access code to something memorable.
        </p>

        <!-- Change access code -->
        <div
          id="block-content-change-acode"
          ref="block-content-change-acode"
          class="flex"
        >
          <div
            @click="_openChangeAccessCode"
            class="change-access-code-input-wrp"
            ref="change-access-code-input-wrp"
          >
            <span>
              <span class="change-access-code-text text-base-80">https://{{ domain }}/</span>
              <input
                autocomplete="off"
                ref="input-change-access-code"
                class="change-access-code-input"
                id="setting-change-access-code-input"
                @keyup.enter="changeAccessCode"
                v-model="newAccessCode"
                :maxlength="10"
                @blur="_outFocus"
                @focus="_onFocus"
                :disabled="isDisableEditPresentation"
              >
            </span>
            <span v-clipboard="getAccessUrl" @click.stop class="copy-btn">
              <aha-icon
                name="copy"
                v-tooltip="'Copy'"
              />
            </span>
          </div>
          <!-- set fixed w-24 to avoid layout jump when saving -->
          <a
            @click="_openChangeAccessCode"
            class="flex items-center justify-center w-24 font-bold text-link--primary"
            v-if="!isDisableEditPresentation"
          >
            <span v-if="!openChangeAccessCode">
              Change
            </span>
            <span @click="changeAccessCode" v-else>
              <span class="flex" v-if="submitting">
                <aha-spinner size="sm" />
                <span class="pl-1">Saving</span>
              </span>
              <span v-if="!submitting">
                <font-awesome-icon
                  icon="check"
                />
                <span class="pl-1">Save</span>
              </span>
            </span>
          </a>
        </div>
        <div v-if="textError" class="mb-2 text-red-100">
          {{ textError }}
        </div>
      </section>

      <!-- Copy link -->
      <section>
        <div class="mb-2 overflow-hidden">
          <div
            v-tooltip="tooltipDownloadQrcode"
            class="flex items-center float-left text-primary-90"
          >
            <i class="mr-1 fas fa-qrcode fa-fw"></i>
            <qr-code-download-link
              class="cursor-pointer font-normal-custom text-primary-90"
              :value="audiencePermanentLink"
            >
              Download QR code
            </qr-code-download-link>
          </div>
        </div>
        <div class="overflow-hidden">
          <div
            class="flex items-center float-left text-primary-90 hover:text-primary-100"
            v-tooltip="tooltipPermanentLink"
          >
            <i class="mr-1 fas fa-link fa-fw"></i>
            <a
              class="cursor-pointer text-primary-90 hover:text-primary-100"
              @click="copyPermanentLink()"
            >Copy permanent link</a>
            <input :value="audiencePermanentLink" id="input-permanent-link">
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import Config from '@/config'
import Utils from '@/util'
import Socket from '@/socket'
import gaCategories from '@/constant/gaCategories'

// components
import QrCodeDownloadLink from '@/components-v2/common/qr-code/QrCodeDownloadLink.vue'
import EventBus from '@/eventBus'

export default {
  components: {
    QrCodeDownloadLink,
  },
  props: {
    presentation: {
      type: Object,
      required: false,
      default() {
        return null
      },
    },
    hasPadding: {
      type: Boolean,
      default: false,
    },
    showHeader: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      openChangeAccessCode: false,
      submitting: false,
      newAccessCode: '',
      textError: '',
      domain: '',
      audienceLink: '',
      presenterLink: '',
      tooltipDownloadQrcode: 'You can download the QR code as an image to use somewhere else (e.g., to print on your event ticket).',
      tooltipPermanentLink: 'This permanent link will never change even if you change the access code above.',
    }
  },
  computed: {
    ...mapState({
      currentUser: (state) => state.currentUser,
      userId: (state) => state.currentUser && state.currentUser.id,
    }),
    audiencePermanentLink() {
      let { uniqueAccessCode } = this.$store.state.presentation

      if (this.presentation && this.presentation.uniqueAccessCode) {
        uniqueAccessCode = this.presentation.uniqueAccessCode
      }

      return `${window.location.protocol}//${this.audienceLink}/${uniqueAccessCode}`
    },
    isDisableEditPresentation() {
      if (this.presentation) {
        return this.presentation.userId !== this.userId &&
          this.presentation.takenOverBy !== this.userId
      }

      return this.$store.state.isDisableEditPresentation
    },
  },
  mounted() {
    this.domain = Config.domain
    this.presenterLink = window.location.origin
    this.audienceLink = Config.audienceLink
    this.newAccessCode = (this.presentation && this.presentation.accessCode)
    || this.$store.state.presentation.accessCode

    if ((this.presentation && this.presentation.whiteLabelLink)
    || this.$store.state.presentation.whiteLabelLink) {
      if (this.presentation && this.presentation.whiteLabelLink) {
        this.audienceLink = this.presentation.whiteLabelLink
        this.domain = this.presentation.whiteLabelLink
      } else {
        this.audienceLink = this.$store.state.presentation.whiteLabelLink
        this.domain = this.$store.state.presentation.whiteLabelLink
      }
    }
    EventBus.$on('update-access-code', this.updateAccessCode)
  },
  destroyed() {
    EventBus.$off('update-access-code', this.updateAccessCode)
  },
  methods: {
    updateAccessCode(newAccessCode) {
      this.newAccessCode = newAccessCode
    },
    _openChangeAccessCode() {
      this.$refs['input-change-access-code'].focus()
      this.$refs['input-change-access-code'].select()
    },
    async changeAccessCode() {
      this.textError = ''
      const newAccessCode = this.newAccessCode.trim().toUpperCase()
      this._outFocus()

      if (!newAccessCode) {
        this.textError = 'Please enter your access code.'
        return
      }

      if (newAccessCode === this.$store.state.presentation.accessCode ||
      (this.presentation && newAccessCode === this.presentation.accessCode)) {
        this.textError = 'This is your current access code.'
        return
      }

      if (this.submitting) return
      this.submitting = true
      let gaTrackingCategories = gaCategories.PRESENTATION_EDITOR
      if (this.$route.name === 'Presentations') {
        gaTrackingCategories = gaCategories.PRESENTATION_LIST
      }

      try {
        const id = (this.presentation && this.presentation.id)
        || this.$store.state.presentation.id
        await this.$http.put(Config.api.changeAccessCode + id, {
          accessCode: newAccessCode,
          socketId: Socket.getSocketId(),
        })
      } catch (error) {
        if (error.body.message) {
          this.textError = error.body.message
        } else {
          Utils.showToastError('Changing access code failed. Please try again.')
        }

        Utils.gaTrackEvent(gaTrackingCategories, 'Invite menu >  Invite participants > Change access code > Failed', newAccessCode)
        throw error
      } finally {
        this.submitting = false
      }

      this.openChangeAccessCode = false
      if (this.presentation && this.presentation.id) { // handle case empty object
        // handle cases:
        // 1. change access code here will also change access code in presentations list
        // 2. blurring access code input when empty
        // eslint-disable-next-line vue/no-mutating-props
        this.presentation.accessCode = newAccessCode
      } else {
        this.$store.state.presentation.accessCode = newAccessCode
      }

      this.$refs['input-change-access-code'].blur()
      Utils.gaTrackEvent(gaTrackingCategories, 'Invite menu > Invite participants > Change access code > Success', newAccessCode)
    },
    _outFocus() {
      this.removeClassActiveInputAccessCode()
      this.newAccessCode = this.$refs['input-change-access-code'].value
      if (
        this.newAccessCode.toUpperCase() ===
        this.$store.state.presentation.accessCode
        || (this.presentation && this.presentation.accessCode === this.newAccessCode.toUpperCase())
      ) {
        return
      }
      const boxChangeAcode = this.$refs['block-content-change-acode']
      if (boxChangeAcode && boxChangeAcode.classList.contains('block-focus')) {
        boxChangeAcode.classList.remove('block-focus')
      }
      if (!this.newAccessCode) {
        if (this.presentation && this.presentation.id) { // handle case empty object
          setTimeout(() => {
            this.$refs[
              'input-change-access-code'
            ].value = this.presentation.accessCode
          }, 200) // setting value with $refs only work with timeout
        } else {
          this.$nextTick(() => {
            setTimeout(() => {
              if (this.$refs[
                'input-change-access-code'
              ]) {
                this.$refs[
                  'input-change-access-code'
                ].value = this.$store.state.presentation.accessCode
              }
            }, 200) // setting value with $refs only work with timeout
          })
        }
        this.openChangeAccessCode = false
        this.submitting = false
      }
    },

    _onFocus() {
      this.openChangeAccessCode = true
      const boxChangeAcode = this.$refs['block-content-change-acode']
      if (boxChangeAcode) {
        boxChangeAcode.classList.add('block-focus')
      }
    },

    removeClassActiveInputAccessCode() {
      const inputWrp = this.$refs['change-access-code-input-wrp']
      if (inputWrp) {
        inputWrp.classList.remove('access-code-input--active')
      }
    },

    getAccessUrl() {
      Utils.showToastSuccess('Link copied!')
      Utils.gaTrackEvent(gaCategories.PRESENTATION_EDITOR, 'Share > Copy access code link')
      return `https://${this.domain}/${this.newAccessCode.toUpperCase()}`
    },

    copyPermanentLink() {
      navigator.clipboard.writeText(this.audiencePermanentLink)
      Utils.showToastSuccess('Link copied!')
      Utils.gaTrackEvent(gaCategories.PRESENTATION_EDITOR, 'Share > Copy permanent link')
    },
  },
}
</script>

<style lang="scss" scoped>

.change-access-code-input-wrp {
  &.access-code-input--active {
    @apply bg-primary-20 border-primary-90;
  }
  padding: 0.4rem;
  @apply appearance-none cursor-pointer flex justify-between;
  @apply text-sm rounded w-full border border-secondary-20;
  @apply focus:bg-primary-20 focus:border-primary-90 focus:outline-none;
  @apply hover:bg-primary-20 hover:border-primary-90;
  .change-access-code-input {
    @apply border-none font-bold outline-none uppercase bg-none cursor-pointer pl-0.5;
    @apply focus:bg-primary-20 focus:outline-none;
    @apply hover:bg-primary-20;
    @apply max-w-24 text-base-90 truncate;
    &:focus {
      background-color: none;
    }
  }

  &:focus {
    @apply border border-base-70;
  }

  &:not(:focus):not(:hover):not(.access-code-input--active) .change-access-code-input:focus {
    @apply bg-base-0;
  }

  &:hover .change-access-code-input {
    @apply bg-primary-20;
  }

  .copy-btn {
    @apply font-bold text-primary-90 flex items-center;
  }
}

.change-access-code-text {
  word-break: break-word;
}

.share__section-title {
  @apply mb-3 text-base font-bold text-secondary-100 flex items-center;
}

.share__section-description {
  @apply mb-3 text-xs;
}

@media (max-width: 580px) {
  .share__presentation-online-link {
    padding-bottom: 60px;
  }
}

#input-permanent-link {
  visibility: hidden;
  position: fixed;
  left: -9999999px;
}

.font-normal-custom {
  font-weight: 400 !important;
}

</style>
